import {
  changeSecondsToDisplayFormat,
  getJustDateFromDateTime,
  changeSecondsToMinutesFormat,
  changeIsoToFrontFormat,
} from '@/utils/momentFacilities';
import { usageStructure, kindProductStructure } from '@/constants/setupBasics';
import { convertNCleanOtherLabel } from '@/utils/dataHandler';
import {
  PPU_INDEX,
  UNLIMITED,
  toolTipDataRoaming,
} from '@/constants/constants';

import { getUrl } from '@/utils/commonUtils';
import {
  getDateBonuTokenization,
  isBonu1gbTokenization,
  isBonuUnlimitedTokenization,
} from './UsageDataHandler';

/* TRANSFORMERS */
/**  Transform from kb to corresponding units
 * @param {number} bytes unit in kb
 * @param {number} decimals number of decimals you want to show
 * @returns {string} converted number with suffix.
 */
export const transformKbtoData = (bytes, decimals) => {
  if (bytes === 0) return '0 KB';
  const k = 1024;
  const dm = decimals || 1;
  const sizes = ['KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const number = parseFloat((bytes / k ** i).toFixed(dm));
  return `${number.toLocaleString('es')} ${sizes[i]}`;
};
const getTextBySMSUnits = (current) => `${current} SMS`;
const getTextByOtherUnits = (current) => `${current} consumos`;

/**  Transform from backend units to display units
 * @param {string} unit measure unit.
 * @param {number} current number to cancat.
 * @returns {string} converted unit with current number.
 */
export const transformMeasureUnits = (unit, current) => {
  if (!unit) {
    return '';
  }
  const traduction = {
    // CAMBIAR PREPAGO
    s: changeSecondsToDisplayFormat,
    unidad: getTextBySMSUnits,
    kb: transformKbtoData,
    otros: getTextByOtherUnits,
  };
  const textToReturn = traduction[unit](current);
  return textToReturn;
};

/**  Transform values from backend to label to display in front for the differnet kind of traffic
 * @param {object} unit object with properties of current, maxValue and unit .
 * @returns {string} converted unit with current number.
 */
export const transformUsageLabelToDisplay = (value) => {
  const unit = transformMeasureUnits(value.unit, value.current);
  const traduction = {
    '-2': ['de ilimitados', `${unit}`],
    '-1': [`${unit}`],
    default: [
      `de  ${transformMeasureUnits(value.unit, value.maxValue)}`,
      `${unit}`,
    ],
  };
  if (value.currentShared !== -1) {
    return traduction[0] || traduction.default;
  }
  return traduction[value.maxValue] || traduction.default;
};

export const changeSecondsToMinutesFormatN = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const remainingSecondsAfterHours = seconds % 3600;
  const minutes = Math.floor(remainingSecondsAfterHours / 60);
  const remainingSeconds = remainingSecondsAfterHours % 60;

  let strReturned = '';

  if (hours > 0) {
    strReturned += `${hours} h `;
  }
  
  if (minutes > 0 || hours > 0) {
    strReturned += `${minutes} min `;
  }
  if (remainingSeconds > 0) {
  strReturned += `${remainingSeconds} seg`;
  }
  
  return strReturned;
};


/**  Transform values from backend to label to display in front for the differnet kind of traffic
 * @param {object} unit object with properties of current, maxValue and unit .
 * @returns {string} converted unit with current number.
 */
export const transformUsageLabelToDisplayPrepay = (value, id = '') => {
  const unit = transformMeasureUnits(value.unitRateBonus, value.current);
  const traduction = {
    '-2': ['de ilimitados', `${unit}`],
    '-1': [`${value.unit}`, `${unit}`],
    H6: [` de ${value.unit}`, `${unit}`],
    HH: [` de ${value.unit}`, `${unit}`],
    default: [
      `de  ${transformMeasureUnits(value.unitRateBonus, value.maxValue)}`,
      `${unit}`,
    ],
  };
  return traduction[value.maxValue] || traduction[id] || traduction.default;
};

/**  Transform values from backend to label to display in front for the differnet kind of traffic
 * @param {object} unit object with properties of current, maxValue and unit .
 * @returns {string} converted unit with current number.
 */
export const transformBonusLabelToDisplay = (bonu) => {
  const traduction = {
    1727: 'Regalo de GB',
    1728: 'Regalo de GB',
    1729: 'Regalo de GB',
    1090:
      bonu.numberTimesContracted > 1
        ? `${bonu.numberTimesContracted} Bonos Internacional 100 activados\n`
        : 'Bono Internacional 100 activado\n',
    1091:
      bonu.numberTimesContracted > 1
        ? `${bonu.numberTimesContracted} Bonos Habla Latino activados\n`
        : 'Bono Habla Latino activado\n',
    1092:
      bonu.numberTimesContracted > 1
        ? `${bonu.numberTimesContracted} Bonos Habla Mundo activados\n`
        : 'Bono Habla Mundo activado\n',
    1642:
      bonu.numberTimesContracted > 1
        ? `${bonu.numberTimesContracted} Bonos Extra 200MB activados\n`
        : 'Bono Internacional 100 activado\n',
    default:
      bonu.numberTimesContracted > 1
        ? `${bonu.numberTimesContracted} Bonos ${bonu.description} activados\n`
        : `Bono ${bonu.description} activado\n`,
  };
  return traduction[bonu.id] || traduction.default;
};

/**  Transform values from backend to label to display in front for the differnet kind of traffic
 * @param {object} unit object with properties of current, maxValue and unit .
 * @returns {string} converted unit with current number.
 */
export const transformBonusLabelToDisplayDetail = (bonu) => {
  const traduction = {
    1727: 'Regalo de GB',
    1728: 'Regalo de GB',
    1729: 'Regalo de GB',
    default: bonu.description,
  };
  return traduction[bonu.id] || traduction.default;
};

/**  Transform from accumulated data usages to donut
 * @param {object} accumulatedUsage product accumulatedUsage
 * @param {object} donutProps {render:bool, icon:{classIcon, label} | label:{color, value}}
 * @returns {object} Component BallProduct JSX.
 */
export const transformFromDataAccumulatedToDataDonutProps = (
  accumulatedUsage,
  donutProps,
  prePay
) => {
  const { data, id } = accumulatedUsage;
  const donutTemplate = donutProps;
  const ringsList = [];
  if (prePay) {
    if (data.unitRateBonus === null) {
      data.unitRateBonus = 'kb';
    }
  }
  /* If currentShared > -1 means that is not a data shared tarif --> doublering */
  if (!data.currentShared || data.currentShared <= -1) {
    let maxValueData;
    let strokeColor;
    if (data.maxValue === PPU_INDEX) {
      maxValueData = data.current;
      strokeColor = 'payPerUse';
      donutTemplate.forcedBadge = false;
    } else if (data.maxValue === UNLIMITED) {
      maxValueData = UNLIMITED;
      strokeColor = 'unlimited';
      donutTemplate.forcedBadge = false;
    } else {
      maxValueData = data.maxValue;
      strokeColor = '';
    }
    const outterRing = {
      consumedData: data.current,
      maxValueData,
      strokeColor,
    };
    ringsList.push(outterRing);
  } else {
    const outterRing = {
      consumedData: data.currentShared,
      maxValueData: data.maxValue,
      strokeColor: '',
    };
    const insiderRing = {
      consumedData: data.current,
      maxValueData: data.maxValue,
      strokeColor: 'gray4',
    };
    ringsList.push(insiderRing);
    ringsList.push(outterRing);
  }
  donutTemplate.ringsList = ringsList;
  if (prePay && data.bonus.length === 0) {
    donutTemplate.usageLabel = transformUsageLabelToDisplayPrepay(data, id);
  } else if (prePay) {
    donutTemplate.usageLabel = transformUsageLabelToDisplayPrepay(data);
  } else {
    donutTemplate.usageLabel = transformUsageLabelToDisplay(data);
  }
  return donutTemplate;
};
// PREPAGO
export const transformFromVoiceAccumulatedToDataDonutProps = (
  accumulatedUsage,
  donutProps
) => {
  const { voice } = accumulatedUsage;
  const donutTemplate = donutProps;
  const ringsList = [];
  /* If currentShared > -1 means that is not a data shared tarif --> doublering */

  let maxValueData;
  let strokeColor;
  if (voice.maxValue === PPU_INDEX) {
    maxValueData = voice.current;
    strokeColor = 'payPerUsePrepaid';
    donutTemplate.forcedBadge = false;
  } else {
    maxValueData = voice.maxValue;
    strokeColor = '';
  }
  const outterRing = {
    consumedData: voice.current,
    maxValueData,
    strokeColor,
  };
  ringsList.push(outterRing);
  donutTemplate.ringsList = ringsList;
  donutTemplate.usageLabel = transformUsageLabelToDisplayPrepay(voice);
  // donutTemplate.usageLabel = 'hola';

  return donutTemplate;
};

export const transformFromVoiceBonoAccumulatedToDataDonutProps = (
  accumulatedUsage,
  donutProps
) => {
  const { voice } = accumulatedUsage;
  const donutTemplate = donutProps;
  const ringsList = [];
  /* If currentShared > -1 means that is not a data shared tarif --> doublering */
  const bonusToPain =
    voice &&
    Array.isArray(voice.bonus) &&
    voice.bonus.length > 0 &&
    voice.bonus.filter((bonu) => bonu.rosco === true);

  const donutTemplateList =
    Array.isArray(bonusToPain) &&
    bonusToPain.length > 0 &&
    bonusToPain.map((filterBono) => {
      let maxValueData;
      let strokeColor;
      if (filterBono.maxValue === PPU_INDEX) {
        maxValueData = filterBono.current;
        strokeColor = 'payPerUsePrepaid';
        donutTemplate.forcedBadge = false;
      } else {
        maxValueData = filterBono.maxValue;
        strokeColor = '';
      }
      const outterRing = {
        consumedData: filterBono.current,
        maxValueData: filterBono.maxValue,
        strokeColor,
      };
      ringsList.push(outterRing);
      donutTemplate.ringsList = ringsList;
      donutTemplate.usageLabel = transformUsageLabelToDisplay(filterBono);
      return donutTemplate;
    });
  // donutTemplate.usageLabel = 'hola';
  return donutTemplateList;
};

export const transformFromSmsAccumulatedToDataDonutProps = (
  accumulatedUsage,
  donutProps
) => {
  const { sms } = accumulatedUsage;
  const donutTemplate = donutProps;
  const ringsList = [];
  let maxValueData;
  let strokeColor;
  if (sms.maxValue === PPU_INDEX) {
    maxValueData = sms.current;
    strokeColor = 'payPerUsePrepaid';
    donutTemplate.forcedBadge = false;
  } else {
    maxValueData = sms.maxValue;
    strokeColor = '';
  }
  const outterRing = {
    consumedData: sms.current,
    maxValueData,
    strokeColor,
  };
  ringsList.push(outterRing);

  donutTemplate.ringsList = ringsList;
  donutTemplate.usageLabel = transformUsageLabelToDisplayPrepay(sms);
  // donutTemplate.usageLabel = 'adios';

  return donutTemplate;
};

export const tranformFromArrayAccumulatedToExtraProps = (
  accumulatedUsagesArray,urlLibrary
) => {
  const accumulatedItemArray = [];
  let totalPrice = 0;
  accumulatedUsagesArray.forEach((accumulatedUsage) => {
    let topText;
    const kindProductObj = kindProductStructure[accumulatedUsage.kindProduct];
    // const bottomText = kindProductObj.visualText;
    const bottomText = '';

    const { icon } = kindProductObj;

    switch (accumulatedUsage.kindProduct) {
      case 'mobile':
        topText = accumulatedUsage.phoneNumber;
        break;
      case 'tv':
        // NOTA cambiar por numbre tv
        topText = 'Televisión';
        break;
      case 'phone':
        topText = accumulatedUsage.phoneNumber;
        break;
      default:
        return null;
    }
    totalPrice = accumulatedUsage.totalExtra + totalPrice;
    const link2use = getUrl('consumption', urlLibrary);
    const usageItem = {
      iconWrapperClass: 'rounded_icon',
      bottomText,
      topText,
      icon,
      price: accumulatedUsage.totalExtra,
      link:
        accumulatedUsage.totalExtra && accumulatedUsage.totalExtra > 0
          ? `${link2use}?productId=${accumulatedUsage.productId}`
          : '',
    };
    accumulatedItemArray.push(usageItem);
    return true;
  });

  const objToReturn = {
    listArray: [
      {
        title: '',
        itemArray: accumulatedItemArray,
      },
    ],
    totalExtraPrice: totalPrice,
  };

  return objToReturn;
};

/**  Transform array of accumulated usages from backend to front display.
 * @param {array} accumulatedUsages array with all the accumulatedUsage
 * @returns {array} array ready to display
 */
export const transformFromArrayAccumulatedToBonusProps = (
  accumulatedUsages,
  prePay = false,
  donutTemplateA = [],
  accumulatedUsageDad,
  id,
  keyFather
) => {
  const idFather = id;
  const donutTemplateArray = donutTemplateA;
  const currentAccumulatedUsages = accumulatedUsages;
  Object.keys(currentAccumulatedUsages).forEach((key) => {
    const accumulatedUsage = currentAccumulatedUsages[key];

    if (!prePay) {
      accumulatedUsage.unit = accumulatedUsageDad.unit;
    } else {
      accumulatedUsage.unitRateBonus = accumulatedUsageDad.unitRateBonus;
    }
    accumulatedUsage.currentShared = accumulatedUsageDad.currentShared;

    let textBotton;
    const render = false;
    const usageLabel = prePay
      ? transformUsageLabelToDisplayPrepay(accumulatedUsage)
      : transformUsageLabelToDisplay(accumulatedUsage);

    if (
      !accumulatedUsage ||
      (!accumulatedUsage.current && !accumulatedUsage.maxValue)
    ) {
      return true;
    }

    let tabText = accumulatedUsage.description;
    if (prePay) {
      tabText = transformBonusLabelToDisplayDetail(accumulatedUsage);
    }

    let donutTemplate = {};

    const ringsList = [];
    let strokeColor;
    let maxValueData = accumulatedUsage.maxValue;
    if (accumulatedUsage.maxValue === -1) {
      strokeColor = prePay ? 'payPerUsePrepaid' : 'payPerUse';
      // NOTA revisar
      donutTemplate.badge = false;
      maxValueData = accumulatedUsage.current;
    } else if (accumulatedUsage.maxValue === -2) {
      strokeColor = 'unlimited';
      donutTemplate.badge = false;
    } else {
      strokeColor = '';
    }

    const outterRing = {
      consumedData: accumulatedUsage.current,
      maxValueData,
      strokeColor,
    };
    ringsList.push(outterRing);

    const badge = accumulatedUsage.extra > 0;
    const wrapperClass = '';
    const color = '';
    donutTemplate = {
      idFather,
      textBotton,
      usageLabel,
      wrapperClass,
      ocultarRosco: accumulatedUsage.ocultarRosco,
      label: {
        color,
        value: tabText,
      },
      render,
      ringsList,
      badge,
      dataFb: keyFather === 'data' && currentAccumulatedUsages[key].fb,
    };

    donutTemplateArray.push(donutTemplate);
    return true;
  });

  return donutTemplateArray;
};

/**  Transform array of accumulated usages from backend to front display.
 * @param {array} accumulatedUsages array with all the accumulatedUsage
 * @returns {array} array ready to display
 */
export const transformFromArrayAccumulatedToUsageProps = (
  accumulatedUsages,
  prePay = false,
  period = 0
) => {
  let id = 0;
  const donutTemplateArray = [];
  const currentAccumulatedUsages = accumulatedUsages;

  if (currentAccumulatedUsages.kindProduct === 'phone') {
    delete currentAccumulatedUsages.data;
    delete currentAccumulatedUsages.sms;
  }

  Object.keys(currentAccumulatedUsages).forEach((key) => {
    const accumulatedUsage = !prePay
      ? currentAccumulatedUsages.period[period][key]
      : currentAccumulatedUsages[key];
    let textBotton;
    let usageLabel;
    const render = false;
    if (key === 'data' && prePay) {
      const { id } = accumulatedUsages;
    }
    switch (key) {
      case 'data':
        textBotton = transformKbtoData(accumulatedUsage.current);
        usageLabel = prePay
          ? transformUsageLabelToDisplayPrepay(accumulatedUsage, id)
          : transformUsageLabelToDisplay(accumulatedUsage);

        break;
      case 'dataRoaming':
        textBotton = transformKbtoData(accumulatedUsage.current);
        usageLabel = prePay
          ? transformUsageLabelToDisplayPrepay(accumulatedUsage, id)
          : transformUsageLabelToDisplay(accumulatedUsage);

        break;
      case 'voice':
        if (!accumulatedUsage.unit) {
          accumulatedUsage.unit = 's';
        }
        textBotton = changeSecondsToDisplayFormat(accumulatedUsage.current);
        usageLabel = prePay
          ? transformUsageLabelToDisplayPrepay(accumulatedUsage)
          : transformUsageLabelToDisplay(accumulatedUsage);
        break;
      case 'sms':
        if (!accumulatedUsage.unit) {
          accumulatedUsage.unit = 'unidad';
        }
        textBotton = accumulatedUsage.current;
        usageLabel = prePay
          ? transformUsageLabelToDisplayPrepay(accumulatedUsage)
          : transformUsageLabelToDisplay(accumulatedUsage);
        break;
      case 'others':
        accumulatedUsage.unit = 'otros';
        textBotton = accumulatedUsage.current;
        usageLabel = transformUsageLabelToDisplay(accumulatedUsage);
        break;
      default:
        break;
    }

    if (
      !accumulatedUsage ||
      (!accumulatedUsage.current && !accumulatedUsage.maxValue)
    ) {
      return true;
    }
    const usageStructureObj = usageStructure[key];

    let wrapperClass2Pain = '';
    let color2Pain = '';
    let tabText2Pain = '';

    if (!usageStructureObj) {
      tabText2Pain = accumulatedUsage.description;
    } else {
      wrapperClass2Pain = usageStructureObj.wrapperClass;
      color2Pain = usageStructureObj.color;
      tabText2Pain = usageStructureObj.tabText;
    }

    let donutTemplate = {};

    const ringsList = [];
    if (accumulatedUsage.currentShared && accumulatedUsage.currentShared > 0) {
      const outterRing = {
        consumedData: accumulatedUsage.currentShared,
        maxValueData: accumulatedUsage.maxValue,
        strokeColor: '',
      };
      const insiderRing = {
        consumedData: accumulatedUsage.current,
        maxValueData: accumulatedUsage.maxValue,
        strokeColor: 'gray4',
      };
      ringsList.push(insiderRing);
      ringsList.push(outterRing);
    } else {
      let strokeColor;
      let maxValueData = accumulatedUsage.maxValue;
      if (accumulatedUsage.maxValue === -1) {
        strokeColor = prePay ? 'payPerUsePrepaid' : 'payPerUse';
        // NOTA revisar
        donutTemplate.badge = false;
        maxValueData = accumulatedUsage.current;
      } else if (accumulatedUsage.maxValue === -2) {
        strokeColor = 'unlimited';
        donutTemplate.badge = false;
      } else {
        strokeColor = '';
      }

      const outterRing = {
        consumedData: accumulatedUsage.current,
        maxValueData,
        strokeColor,
      };
      ringsList.push(outterRing);
    }

    const badge = accumulatedUsage.extra > 0;

    donutTemplate = {
      id: key === 'dataRoaming' ? 0 : id,
      idFather: key === 'dataRoaming' ? 0 : false,
      textBotton,
      usageLabel,
      wrapperClass2Pain,
      label: {
        color2Pain,
        value: key === 'dataRoaming' ? 'Datos Roaming' : tabText2Pain,
        toolTip: key === 'dataRoaming' ? toolTipDataRoaming : false,
      },
      render,
      ringsList,
      badge,
    };

    if (
      !(
        key === 'data' &&
        accumulatedUsage.bonus &&
        accumulatedUsage.bonus.length > 0
      )
    ) {
      if (!(key === 'dataRoaming' && accumulatedUsage.current === 0)) {
        donutTemplateArray.push(donutTemplate);
      }
    }

    if (accumulatedUsage.bonus && accumulatedUsage.bonus.length > 0) {
      transformFromArrayAccumulatedToBonusProps(
        accumulatedUsage.bonus,
        prePay,
        donutTemplateArray,
        accumulatedUsage,
        id,
        key
      );
    }
    if (key !== 'dataRoaming') {
      id += 1;
    }
    return true;
  });

  return donutTemplateArray;
};

/**  Transform array of accumulated usages from backend to front display.
 * @param {array} accumulatedUsages array with all the accumulatedUsage
 * @returns {array} array ready to display
 */
export const transformFromArrayAccumulatedToProgressBar = (
  accumulatedUsages,
  period = 0
) => {
  const bonusTemplateArray = [];
  const groups = [];

  let multipleBonusSameKind = false;
  let usage;
  let extraExpenses = 0;
  Object.keys(usageStructure).forEach((key) => {
    usage =
      accumulatedUsages && accumulatedUsages.period
        ? accumulatedUsages.period[period][key]
        : accumulatedUsages[key];

    if (usage) {
      if (usage && usage.bonus.length > 1) {
        multipleBonusSameKind = true;
      }

      if (usage && usage.bonus.length > 0) {
        usage.bonus.forEach((bonu) => {
          let label2Use = bonu.description;
          const dateBonuTokenization = changeIsoToFrontFormat(
            getDateBonuTokenization(bonu)
          );
          const isBonu1gb7dTokenization = isBonu1gbTokenization(bonu);
          const isBonuTokenizationUnlimited = isBonuUnlimitedTokenization(bonu);
          if (isBonu1gb7dTokenization) {
            label2Use = `Bono 1GB 7 días en tokens ${
              dateBonuTokenization ? `  hasta ${dateBonuTokenization}` : ''
            }`;
          } else if (isBonuTokenizationUnlimited) {
            label2Use = `Datos ilimitados en tokens ${
              dateBonuTokenization ? `  hasta ${dateBonuTokenization}` : ''
            }`;
          }
          bonusTemplateArray.push({
            mainColor: '',
            currentValue: bonu.current,
            maxValue: bonu.maxValue,
            label: label2Use,
            type: key,
          });
        });
        groups.push(key);
      } else if (usage && usage.current >= 0) {
        const usageStructureObj = usageStructure[key];

        const description = usageStructureObj
          ? usageStructureObj.ballText
          : key;
        const color = usageStructureObj ? usageStructureObj.color : 'black';

        bonusTemplateArray.push({
          mainColor: color,
          currentValue: usage.current,
          maxValue: usage.maxValue,
          label: description,
          type: key,
        });
        groups.push(key);
      }
      extraExpenses += usage.extra;
    }
  });

  const progressBar = {
    extraExpenses,
    bonusTemplate: bonusTemplateArray,
    groups,
    multipleBonusSameKind,
  };

  return progressBar;
};

/**  Transform array of accumulated usages from backend to front display.
 * @param {array} accumulatedUsages array with all the accumulatedUsage
 * @returns {array} array ready to display
 */
export const transformFromArrayAccumulatedToBallUsage = (accumulatedUsages) => {
  const usageVoice = accumulatedUsages.voice;
  const usageOther = accumulatedUsages.other;
  const ballUsageArray = [
    {
      id: 0,
      textTop: 'Voz',
      textBotton: '',
      wrapperClass: 'greenBall',
      badge: true,
      label: {
        value: `Llamadas ${usageVoice.current}${usageVoice.unit}`,
        color: 'green',
      },
    },
    {
      id: 1,
      textTop: `Otros ${usageOther.current}`,
      textBotton: '',
      wrapperClass: 'blueBall',
      badge: false,
      label: {
        value: 'Otros 3',
        color: 'green',
      },
    },
  ];

  return ballUsageArray;
};

export const getItemListByUsage = (usage) => {
  let item;
  switch (usage.kindUsage) {
    case 'data':
      item = {
        topText: 'Conexión',
        bottomText: transformKbtoData(usage.used),
        price: usage.price,
      };
      break;
    case 'voice':
      item = {
        topText: usage.numberDestination,
        bottomText: `${
          usage.dateUsage.split(' ')[1]
        } - ${changeSecondsToMinutesFormat(usage.used)}`,
        price: usage.price,
      };
      break;
    case 'sms':
      item = {
        topText: usage.numberDestination,
        bottomText: `${usage.dateUsage.split(' ')[1]} - 1 ${usage.serviceType}`,
        price: usage.price,
      };
      break;
    case 'others':
      item = {
        topText: convertNCleanOtherLabel(usage.label),
        bottomText: usage.dateUsage.split(' ')[1],
        price: usage.price,
      };
      break;
    default:
  }

  return item;
};

export const transformGroupedUsagesToListProperties = (groupedUsages) => {
  const listArray = [];
  Object.keys(groupedUsages).forEach((groupedUsageKey) => {
    const dateString = groupedUsageKey.replace('day_', '');
    const title = getJustDateFromDateTime(dateString);
    const itemArray = [];
    groupedUsages[groupedUsageKey].forEach((groupedUsage) => {
      const itemList = getItemListByUsage(groupedUsage);
      itemArray.push(itemList);
    });

    listArray.push({
      title,
      itemArray,
    });
  });

  return listArray;
};

export const transformStateBonus = (state) => {
  const traduction = {
    // BONU_ACTIVATE: 'ACTIVADOS',
    // BONU_CONSUMED: 'CONSUMIDOS',
    0: 'ACTIVADOS',
    1: 'CONSUMIDOS',
    default: '',
  };
  return traduction[state] || traduction.default;
};

export const transformStateBonusTolabel = (state) => {
  const traduction = {
    // BONU_ACTIVATE: 'ACTIVADOS',
    // BONU_CONSUMED: 'CONSUMIDOS',
    0: 'caducan',
    1: 'consumido',
    default: '',
  };
  return traduction[state] || traduction.default;
};

export const transformBonusManagementLabelToDisplay = (contractedBond) => {
  const headLabel =
    contractedBond.numberTimesContracted > 1
      ? `${contractedBond.numberTimesContracted} Bonos ${contractedBond.description}`
      : `Bono ${contractedBond.description}`;
  const tailLabel = transformStateBonusTolabel(contractedBond.state);
  return `${headLabel} ${tailLabel} ${contractedBond.endDate}`;
};
